// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./CoreRepairScopes.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../context/UserContext";

// MUI COMPONENTS
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Snackbar,
} from "@mui/material";

// MUI ICONS
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// CUSTOM COMPONENTS
import CoreRepairScopeDeleteDialog from "./CoreRepairScopeDeleteDialog/CoreRepairScopeDeleteDialog";
import CoreRepairScopeEditAddDialog from "./CoreRepairScopesEditAddDialog/CoreRepairScopesEditAddDialog";
import TanStackTable from "../Misc/Table/TanStackTable";
import J2NoResultsFound from "../Misc/J2NoResultsFound/J2NoResultsFound";

// CONSTANTS
import {
	defaultSort,
	notApplicable,
	splitSort,
} from "../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const CoreRepairScopes = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [coreRepairScopes, setCoreRepairScopes] = useState([]);
	const [coreRepairScopesRows, setCoreRepairScopesRows] = useState([]);
	const [coreRepairScopesColumns, setCoreRepairScopesColumns] = useState([]);

	const [assemblies, setAssemblies] = useState([]);
	const [buildingElements, setBuildingElements] = useState([]);
	const [repairScopeSpecifications, setRepairScopeSpecifications] = useState(
		[]
	);
	const [repairScopeAssemblies, setRepairScopeAssemblies] = useState([]);

	const [allSpecifications, setAllSpecifications] = useState([]);
	const [recordToEdit, setRecordToEdit] = useState({});
	const [recordToDelete, setRecordToDelete] = useState({});

	const [loading, setLoading] = useState(false);
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Core Repair Scopes",
			employeeID: employeeID,
			notes: "Accessed the Core Repair Scopes page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		if (
			coreRepairScopes.length > 0 &&
			assemblies.length > 0 &&
			allSpecifications.length > 0
		) {
			createRowData();
			createColumnData();
		}
	}, [coreRepairScopes, assemblies, allSpecifications]);

	// INPUT HANDLERS

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = async (doRefresh) => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({}); // clear record

		if (doRefresh) {
			await handleAPICalls(); // refresh data
		}
	};

	const handleDeleteClick = async (record) => {
		setRecordToDelete(record);
		setDeleteDialogOpen(true);
	};

	const handleDeleteDialogClose = async (doRefresh) => {
		setDeleteDialogOpen(false);
		setRecordToDelete({}); // clear record

		if (doRefresh) {
			await handleAPICalls(); // refresh data
		}
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await fetchCoreRepairScopes();
		await fetchAssemblies();
		await fetchBuildingElements();
		await fetchCoreRepairScopeSpecifications();
		await fetchRepairScopeAssemblies();
		await fetchAllSpecifications();
		setLoading(false);

		return null;
	};

	const fetchCoreRepairScopes = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/coreRepairScopes/all"
			);
			setCoreRepairScopes(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchAssemblies = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/encompass/assemblies/all"
			);
			setAssemblies(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchBuildingElements = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/coreRepairScopes/buildingElements/all"
			);
			setBuildingElements(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchCoreRepairScopeSpecifications = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/coreRepairScopes/repairScopeSpecifications/all"
			);
			setRepairScopeSpecifications(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchRepairScopeAssemblies = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/coreRepairScopes/repairScopeAssemblies/all"
			);
			setRepairScopeAssemblies(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const fetchAllSpecifications = async () => {
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/specifications/allStandardSpecifications"
			);
			// exclude specs with parent_id
			res.data = res.data.filter((spec) => spec.parent_id === null);
			setAllSpecifications(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const copyDescription = (description) => {
		navigator.clipboard
			.writeText(description)
			.then(() => {
				setSnackbarMessage("copied to clipboard");
				setOpenSnackbar(true);
			})
			.catch((error) => {
				console.error("Failed to copy description: ", error);
				setSnackbarMessage("failed to copy");
			});
	};

	const snackbarActions = () => {
		return (
			<>
				<Button onClick={() => setOpenSnackbar(false)}>Close</Button>
			</>
		);
	};

	const createColumnData = () => {
		setCoreRepairScopesColumns([
			{
				accessorKey: "building_type",
				header: () => <span>Building Type</span>,
				label: "Building Type",
				cell: ({ row }) => (
					<p>{row.original.building_type || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "split_assemblies",
				header: () => <span>Related Assemblies</span>,
				label: "Related Assemblies",
				cell: ({ row }) =>
					row.original.split_assemblies ? (
						<div>
							{row.original.split_assemblies
								? row.original.split_assemblies
										.split("_SPLIT_")
										.map((spec) => <div>{spec}</div>)
								: null}
						</div>
					) : (
						notApplicable
					),
				filterType: "checkbox",
				filterOptions: assemblies.map((assembly) => ({
					label: assembly.name,
					value: assembly.id,
				})),
				filterFn: splitSort,
			},
			{
				accessorKey: "description",
				header: () => <span>Description</span>,
				label: "Description",
				cell: ({ row }) => (
					<p>{row.original.description || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "replacement_material",
				header: () => <span>Replacement Material</span>,
				label: "Replacement Material",
				cell: ({ row }) => (
					<p>{row.original.replacement_material || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "split_specifications",
				header: () => <span>Related Specifications</span>,
				label: "Related Specifications",
				cell: ({ row }) =>
					row.original.split_specifications ? (
						<div>
							{row.original.split_specifications
								? row.original.split_specifications
										.split("_SPLIT_")
										.map((spec) => <div>{spec}</div>)
								: null}
						</div>
					) : (
						notApplicable
					),
				filterType: "checkbox",
				filterOptions: allSpecifications.map((spec) => ({
					label: spec.title,
					value: spec.id,
				})),
				filterFn: splitSort,
			},
			{
				id: "copy_edit_delete",
				header: "",
				cell: ({ row }) => (
					<Box display="flex" justifyContent="end" gap={1}>
						<IconButton
							onClick={() =>
								copyDescription(row.original.description)
							}
							disableRipple
							sx={{ padding: 0 }}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
						<IconButton
							onClick={() => handleEditClick(row.original)}
							disableRipple
							sx={{ padding: 0 }}
						>
							<EditIcon fontSize="small" />
						</IconButton>
						<IconButton
							onClick={() => handleDeleteClick(row.original)}
							disableRipple
							sx={{ padding: 0 }}
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</Box>
				),
				enableColumnFilter: false,
				enableSorting: false,
			},
		]);
	};

	const createRowData = () => {
		setCoreRepairScopesRows(
			coreRepairScopes.map((crs) => ({
				building_type: crs.building_type,
				split_assemblies: crs.split_assemblies,
				description: crs.description,
				replacement_material: crs.replacement_material,
				split_specifications: crs.split_specifications,
				id: crs.id,
			}))
		);
	};

	// RENDER
	return (
		<div className="CoreRepairScopes">
			<div className="CoreRepairScopesHeader">
				<div className="CoreRepairScopesHeaderTitle">
					Core Repair Scopes
					{props.isAdmin ? (
						<Button
							variant="contained"
							color="primary"
							onClick={handleAddClick}
							disabled={loading}
						>
							New Record
						</Button>
					) : null}
				</div>
			</div>
			{loading ? (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			) : (
				<>
					{coreRepairScopes.length === 0 && <J2NoResultsFound />}
					{coreRepairScopes.length > 0 &&
						coreRepairScopesRows.length > 0 &&
						coreRepairScopesColumns.length > 0 && (
							<div className="tableContainerDiv">
								<TanStackTable
									columnData={coreRepairScopesColumns}
									rowData={coreRepairScopesRows}
								/>
							</div>
						)}
				</>
			)}
			<Snackbar
				open={openSnackbar}
				onClose={() => {
					setOpenSnackbar(false);
					setSnackbarMessage("");
				}}
				message={snackbarMessage}
				autoHideDuration={6000}
				action={snackbarActions()}
			/>
			<CoreRepairScopeEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				recordToEdit={recordToEdit}
				assemblies={assemblies}
				buildingElements={buildingElements}
				editing={editing}
				setSnackbarMessage={setSnackbarMessage}
				setOpenSnackbar={setOpenSnackbar}
				repairScopeSpecifications={repairScopeSpecifications}
				repairScopeAssemblies={repairScopeAssemblies}
				allSpecifications={allSpecifications}
			/>
			<CoreRepairScopeDeleteDialog
				open={deleteDialogOpen}
				onClose={handleDeleteDialogClose}
				recordToDelete={recordToDelete}
				setSnackbarMessage={setSnackbarMessage}
				setOpenSnackbar={setOpenSnackbar}
				repairScopeSpecifications={repairScopeSpecifications}
			/>
		</div>
	);
};

// EXPORT
export default CoreRepairScopes;
